import { Link } from 'react-router-dom';
import './style.css';

const LAST_UPDATED = 'June 11, 2026';

export default function PrivacyPolicyPage() {
    return (
        <div className="privacy-policy">
            <div className="privacy-policy__content">
                <Link className="privacy-policy__back" to="/">&larr; Back</Link>

                <h1>Privacy Policy</h1>
                <p className="privacy-policy__updated">Last updated: {LAST_UPDATED}</p>

                <p>
                    OPTCG.dev (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) operates an online simulator for the
                    One Piece Trading Card Game at <a href="https://optcg.dev">https://optcg.dev</a>. This Privacy Policy
                    explains how we collect, use, and share information when you use our website and services.
                </p>

                <p>
                    OPTCG.dev is a fan-made project and is not affiliated with, endorsed by, or sponsored by Bandai or
                    any official One Piece Trading Card Game entity.
                </p>

                <h2>Information We Collect</h2>

                <h3>Account information</h3>
                <p>
                    When you sign in, we receive information from the provider you choose:
                </p>
                <ul>
                    <li>
                        <strong>Discord:</strong> your Discord user ID, username, and avatar. If you are a member of our
                        Discord server, we may also read your server roles to determine play-testing or admin access.
                    </li>
                    <li>
                        <strong>Google:</strong> your email address, profile picture, and a display name derived from your
                        email address.
                    </li>
                </ul>
                <p>
                    We do not receive or store your Discord or Google password. OAuth tokens from those providers are used
                    only during sign-in and are not stored on our servers.
                </p>

                <h3>Gameplay and user content</h3>
                <ul>
                    <li>
                        <strong>Decks:</strong> if you create or save decks, we store the deck name, leader, card list, and
                        your account identifier.
                    </li>
                    <li>
                        <strong>Game activity:</strong> we may store basic metadata about recent games, such as a game
                        identifier, participating usernames, and start and end times.
                    </li>
                    <li>
                        <strong>Real-time gameplay:</strong> during active games, game state is processed in memory to
                        support multiplayer play. That live state is not permanently stored after the game ends.
                    </li>
                </ul>

                <h3>Technical information</h3>
                <p>
                    Like most web services, our servers may automatically receive standard technical data when you use the
                    site, such as your IP address, browser type, device information, and the pages or actions you request.
                    We may also generate server logs for debugging, security, and operational purposes.
                </p>

                <h2>How We Use Information</h2>
                <p>We use the information we collect to:</p>
                <ul>
                    <li>Authenticate you and maintain your session</li>
                    <li>Provide multiplayer gameplay, deck building, and related features</li>
                    <li>Enforce access controls, such as play-testing and admin permissions</li>
                    <li>Monitor service performance, troubleshoot issues, and protect against abuse</li>
                    <li>Improve and operate OPTCG.dev</li>
                </ul>

                <h2>Cookies</h2>
                <p>
                    We use a strictly necessary authentication cookie named <code>token</code> to keep you signed in. This
                    cookie is HTTP-only, sent over secure connections in production, and expires after one hour. You can
                    sign out at any time via the sign-out link, which clears this cookie.
                </p>
                <p>
                    We do not use advertising or third-party analytics cookies on OPTCG.dev.
                </p>

                <h2>Third-Party Services</h2>
                <p>We rely on the following third parties to operate the service:</p>
                <ul>
                    <li>
                        <strong>Discord</strong> and <strong>Google</strong> for sign-in. Their use of your data is
                        governed by their own privacy policies.
                    </li>
                    <li>
                        <strong>Google Fonts</strong> and the <strong>Google Identity Services</strong> script, which may
                        receive technical information from your browser when those resources load.
                    </li>
                    <li>
                        <strong>Hosting and database providers</strong>, including Google Cloud infrastructure and MongoDB,
                        where application data is stored and processed.
                    </li>
                    <li>
                        <strong>Official card image sources</strong>, accessed through our image proxy when displaying card
                        artwork.
                    </li>
                </ul>
                <p>
                    We do not sell your personal information.
                </p>

                <h2>Data Retention</h2>
                <p>
                    We retain account-related and deck data for as long as your account remains active or as needed to
                    provide the service. Recent game metadata may be retained for operational and analytics purposes.
                    Server logs are retained for a limited period and then deleted or aggregated.
                </p>

                <h2>Your Choices and Rights</h2>
                <p>You may:</p>
                <ul>
                    <li>Sign out at any time to end your authenticated session</li>
                    <li>Revoke OPTCG.dev&apos;s access through your Discord or Google account settings</li>
                    <li>Contact us to request access to, correction of, or deletion of data associated with your account</li>
                </ul>
                <p>
                    Depending on where you live, you may have additional privacy rights under applicable law. We will
                    respond to valid requests in accordance with those laws.
                </p>

                <h2>Children</h2>
                <p>
                    OPTCG.dev is not directed at children under 13, and we do not knowingly collect personal information
                    from children under 13. If you believe a child has provided us personal information, please contact us
                    so we can delete it.
                </p>

                <h2>Security</h2>
                <p>
                    We use reasonable technical and organizational measures to protect information, including secure
                    cookies and authenticated API access. No method of transmission or storage is completely secure.
                </p>

                <h2>International Users</h2>
                <p>
                    If you access OPTCG.dev from outside the country where our servers are located, your information may
                    be transferred to and processed in that country or other locations where our service providers operate.
                </p>

                <h2>Changes to This Policy</h2>
                <p>
                    We may update this Privacy Policy from time to time. When we do, we will revise the &quot;Last
                    updated&quot; date above. Continued use of the service after changes become effective means you accept
                    the updated policy.
                </p>

                <h2>Contact Us</h2>
                <p>
                    If you have questions about this Privacy Policy or want to make a privacy-related request, contact us
                    through our{' '}
                    <a href="https://discord.gg/TRkBnW73Bu" target="_blank" rel="noreferrer">
                        Discord server
                    </a>.
                </p>
            </div>
        </div>
    );
}
